import { useMemo, useRef, useState, type JSX } from "react";

import { DataTable } from "@impero-com/common/components/dataTable";
import * as Cmp from "@impero-com/common/utils/comparison";
import { getParentElement } from "@impero-com/common/utils/dom";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { SimpleRowClickHandler } from "@impero-com/common/utils/rowClickHandler";
import memoizeOne from "memoize-one";

import {
    NotificationType,
    InitialAssignmentKind,
    SubsequentAssignmentKind,
    UserGroupViewDtoContent,
    UserOverviewDtoContent,
    NotificationRecipientKind,
    UserGroupView,
    UserOverview,
    EntityView,
} from "api/generated/model";
import { UserOrUserGroupTypeahead } from "components/userTypeahead";
import {
    AssignmentGroupId,
    ControlAssignmentData,
    ControlAssignmentGroup,
    controlAssignmentDataToControlAssignmentGroups,
    updateControlAssignmentData,
} from "utils/controls/assignmentUtils";
import { ASSIGNMENT_EXTRACTOR_KEYS } from "utils/controls/extractorKeys";
import { Guid } from "utils/guid";
import * as guid from "utils/guid";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";
import { UserOrUserGroupOverview } from "utils/user";

import { DeleteCell, NotifyCell } from "./cellWidgets";
import { mapPropsToTableDataSource } from "./dataSource";
import { AssignmentDrawer, DrawerCloseAction } from "./drawer";
import { toUserOrUserGroupOverviewEnum } from "./drawer/types";
import { AssignmentIdToSodIssues } from "./getAssignmentIdToSodIssues";
import { validateSod as rawValidateSod } from "./segregationOfDuties";
import { buildColumns } from "./table";
import * as theme from "./theme.css";
import { AssignmentRow, ControlAssignmentResources } from "./types";

export interface EditAssignmentsTableProps {
    value: ControlAssignmentData;
    onChange: (data: ControlAssignmentData) => void;
    assignmentIdToSodIssues: AssignmentIdToSodIssues;
    resources: ControlAssignmentResources;
    onDeleteRow: (assignmentId: Guid) => void;
    notificationRecipients: Record<
        AssignmentGroupId,
        Partial<Record<NotificationType, NotificationRecipientKind[]>>
    >;
    controlProgramId: number | null;
    isRowClickable: (assignmentGroupId: AssignmentGroupId) => boolean;
    entitiesById: Map<number, EntityView>;
}

export type DrawerState =
    | { type: "EDIT"; idx: AssignmentGroupId }
    | { type: "CREATE"; idx: AssignmentGroupId };

const validateSod = memoizeOne(rawValidateSod);

export const computeCreateSod = memoizeOne(
    (
        assigneeKind: InitialAssignmentKind,
        firstReviewerKind: SubsequentAssignmentKind | undefined,
        secondReviewerKind: SubsequentAssignmentKind | undefined,
        segregationOfDutiesEnabled: boolean
    ) => {
        if (!firstReviewerKind || !segregationOfDutiesEnabled) {
            return undefined;
        }
        const finalReviewerKind = secondReviewerKind ?? firstReviewerKind;
        const validationResult = validateSod(
            assigneeKind,
            finalReviewerKind,
            secondReviewerKind ? firstReviewerKind : undefined
        );
        switch (validationResult.type) {
            case "NoSodIssues":
                return undefined;
            case "SodIssues":
                return validationResult;
            default:
                return assertNever(validationResult);
        }
    }
);

export const EditAssignmentsTable = ({
    onDeleteRow,
    resources,
    value,
    onChange,
    assignmentIdToSodIssues,
    controlProgramId,
    isRowClickable,
    entitiesById,
}: EditAssignmentsTableProps): JSX.Element => {
    const controlEntitiesEnabled = useFeatureFlag("controlEntities");
    const assignmentGroups =
        controlAssignmentDataToControlAssignmentGroups(value);

    const [resourceValue, dispatchResourceValue] = useState(resources);
    const [drawerState, setDrawerState] = useState<DrawerState | undefined>(
        undefined
    );
    // Snapshot of the group being edited, for reset on cancel
    const editSnapshotRef = useRef<ControlAssignmentGroup | undefined>(
        undefined
    );

    const selectedGroup = drawerState
        ? assignmentGroups.find((g) => g.id === drawerState.idx)
        : undefined;

    const dataView = useMemo(() => {
        const dataView = mapPropsToTableDataSource(
            assignmentGroups,
            resourceValue,
            controlEntitiesEnabled
        );
        const stringCompare = Cmp.string();
        const extractors = dataView.extractorGroups[0].extractors;
        extractors.push({
            id: ASSIGNMENT_EXTRACTOR_KEYS.NOTIFY,
            label: t("Control_Assignment_NotifyLabel"),
            getValue: (row) => row,
            compare: Cmp.map((row) => row.id, stringCompare),
            asString: (_row) => "",
        });
        extractors.push({
            id: "delete",
            label: "",
            getValue: (row) => row,
            compare: Cmp.map((row) => row.id, stringCompare),
            asString: (_row) => "",
        });

        return dataView;
    }, [
        resourceValue,
        // eslint-disable-next-line react-hooks/preserve-manual-memoization
        assignmentGroups,
        controlEntitiesEnabled,
    ]);

    const columnBuilders = useMemo(() => {
        const innerColumnBuilders = buildColumns(entitiesById);

        innerColumnBuilders[ASSIGNMENT_EXTRACTOR_KEYS.NOTIFY] = (
            _extractor
        ) => ({
            getFormattedValue: (assignmentRow) => {
                const assignment = assignmentGroups.find(
                    (a) => a.id === assignmentRow.id
                );
                const allCompleted =
                    assignment?.notificationRecipients?.["AllCompleted"] ?? [];

                const numberOfUserRecipients = allCompleted.filter(
                    (recipient) => recipient.type === "User"
                ).length;
                const numberOfGroupRecipients = allCompleted.filter(
                    (recipient) => recipient.type === "Group"
                ).length;

                return (
                    <NotifyCell
                        assignmentRow={assignmentRow}
                        numberOfUserRecipients={numberOfUserRecipients}
                        numberOfGroupRecipients={numberOfGroupRecipients}
                        disabled={!isRowClickable(assignmentRow.id)}
                    />
                );
            },
            sortable: false,
        });
        innerColumnBuilders["delete"] = (_extractor) => ({
            getFormattedValue: (row) => (
                <DeleteCell
                    assignmentId={row.id}
                    onClick={onDeleteRow}
                    disabled={!isRowClickable(row.id)}
                />
            ),
            sortable: false,
        });
        return innerColumnBuilders;
        // eslint-disable-next-line react-hooks/preserve-manual-memoization
    }, [assignmentGroups, isRowClickable, onDeleteRow, entitiesById]);

    const renderFooter = () => {
        return (
            <tr className="ant-table-row">
                <td>
                    <UserOrUserGroupTypeahead
                        id="new-responsible-typeahead"
                        value={undefined}
                        onSelect={handleNewAssignment}
                        placeholder={t(
                            "CreateControl_Assignees_NewResponsible"
                        )}
                        getPopupContainer={getParentElement}
                    />
                </td>
                <td />
                <td />
                <td />
                <td />
                <td />
                {controlEntitiesEnabled && <td />}
            </tr>
        );
    };

    const getRowClassName = (row: AssignmentRow, isActiveRow: boolean) => {
        const clickable = isRowClickable(row.id);
        if (!clickable) {
            return theme.disabledRow;
        }
        const hasError = !!assignmentIdToSodIssues[row.id];
        if (hasError) {
            return isActiveRow ? theme.activeErrorRow : theme.errorRow;
        } else {
            return isActiveRow ? theme.activeRow : theme.row;
        }
    };

    const handleRowClick = (assignmentRow: AssignmentRow) => {
        const group = assignmentGroups.find((g) => g.id === assignmentRow.id);
        editSnapshotRef.current = group ? { ...group } : undefined;
        setDrawerState({
            type: "EDIT",
            idx: assignmentRow.id,
        });
    };

    const rowClickHandler = memoizeOne(() => {
        const handler: SimpleRowClickHandler<AssignmentRow> = {
            type: "SimpleRowClickHandler",
            onClick: (assignmentRow) => {
                if (isRowClickable(assignmentRow.id)) {
                    handleRowClick(assignmentRow);
                }
            },
        };
        return handler;
    });

    const handleUserAdded = (user: UserOverview) => {
        dispatchResourceValue((resources) => {
            const maybeOldUserIndex = resourceValue.users.findIndex(
                (userDto) => userDto.resource.id === user.id
            );

            let users: UserOverviewDtoContent[];
            if (maybeOldUserIndex < 0) {
                users = [
                    ...resources.users,
                    {
                        resource: user,
                        actions: {
                            edit: false,
                        },
                    },
                ];
            } else {
                resourceValue.users[maybeOldUserIndex].resource = user;
                users = [...resourceValue.users];
            }

            return {
                ...resources,
                users,
            };
        });
    };

    const handleUserGroupAdded = (userGroup: UserGroupView) => {
        dispatchResourceValue((resources) => {
            const maybeOldUserGroupIndex = resources.userGroups.findIndex(
                (userGroupDto) => userGroupDto.resource.id === userGroup.id
            );

            let userGroups: UserGroupViewDtoContent[];
            if (maybeOldUserGroupIndex < 0) {
                userGroups = [
                    ...resources.userGroups,
                    {
                        resource: userGroup,
                        actions: {
                            edit: false,
                            admin: false,
                            delete: false,
                        },
                    },
                ];
            } else {
                resourceValue.userGroups[maybeOldUserGroupIndex].resource =
                    userGroup;
                userGroups = [...resourceValue.userGroups];
            }

            return {
                ...resources,
                userGroups,
            };
        });
    };

    const updateGroup = (updatedGroup: ControlAssignmentGroup) => {
        const updatedGroups = assignmentGroups.map((g) =>
            g.id === updatedGroup.id ? updatedGroup : g
        );
        onChange(updateControlAssignmentData(value, updatedGroups));
    };

    const handleDrawerClose = (action: DrawerCloseAction) => {
        if (!drawerState) {
            return;
        }

        switch (action) {
            case "save":
                // Changes were already applied via updateGroup
                break;
            case "remove": {
                // Remove the group (CREATE + cancel)
                const filtered = assignmentGroups.filter(
                    (g) => g.id !== drawerState.idx
                );
                onChange(updateControlAssignmentData(value, filtered));
                break;
            }
            case "reset": {
                // Restore the snapshot (EDIT + cancel)
                if (editSnapshotRef.current) {
                    const restored = assignmentGroups.map((g) =>
                        g.id === drawerState.idx ? editSnapshotRef.current! : g
                    );
                    onChange(updateControlAssignmentData(value, restored));
                }
                break;
            }
        }

        editSnapshotRef.current = undefined;
        setDrawerState(undefined);
    };

    const buildDrawer = () => {
        const { segregationOfDutiesEnabled } = value;

        if (drawerState && selectedGroup) {
            return (
                <AssignmentDrawer
                    type={drawerState.type}
                    onClose={handleDrawerClose}
                    resources={resourceValue}
                    segregationOfDutiesEnabled={segregationOfDutiesEnabled}
                    value={selectedGroup}
                    onChange={updateGroup}
                    onUserAdded={handleUserAdded}
                    onUserGroupAdded={handleUserGroupAdded}
                    controlProgramId={controlProgramId}
                    entitiesById={entitiesById}
                />
            );
        } else {
            return null;
        }
    };

    const handleNewAssignment = (rawResponsible: UserOrUserGroupOverview) => {
        const responsible = toUserOrUserGroupOverviewEnum(rawResponsible);
        let initialAssignment: InitialAssignmentKind;
        switch (responsible.type) {
            case "User": {
                handleUserAdded(responsible.user);
                initialAssignment = {
                    type: "User",
                    userId: responsible.user.id,
                };
                break;
            }
            case "UserGroup": {
                handleUserGroupAdded(responsible.userGroup);
                initialAssignment = {
                    type: "Pool",
                    userGroupId: responsible.userGroup.id,
                    defaultClaimantId: null,
                };
                break;
            }
        }
        const id = guid.generate();
        const newGroup: ControlAssignmentGroup = { id, initialAssignment };
        const updatedGroups = [...assignmentGroups, newGroup];
        onChange(updateControlAssignmentData(value, updatedGroups));

        setDrawerState({
            type: "CREATE",
            idx: id,
        });
    };

    return (
        <>
            <DataTable
                key={`parent-with-${controlEntitiesEnabled}`} // force remount when entity linking feature flag changes to reset internal state of DataTable
                id="assignmentData"
                dataView={dataView}
                columnBuilders={columnBuilders}
                rowClickHandler={rowClickHandler()}
                footerRenderer={renderFooter}
                hidePlaceholder={true}
                getRowClassName={getRowClassName}
                getRowCountLabel={(shown, count) =>
                    t("ControlAssignment_RowCount", { shown, count })
                }
                getSinglePageRowCountLabel={(count) =>
                    t("ControlAssignment_RowCountSinglePage", { count })
                }
            />
            {buildDrawer()}
        </>
    );
};
